import React, { useState, useEffect } from 'react';
import { Table, Tag, Typography, Space, Statistic, Card, Button, message } from 'antd';
import { TrophyOutlined, EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import AdminLayout from '../Layout/AdminLayout';
import axiosInstance from '../../utils/axiosConfig';

const { Text } = Typography;

const AREA_TAG_COLOR = { KV1: 'blue', 'KV2-NT': 'cyan', KV2: 'green', KV3: 'default' };

const PRIZE_LABEL = { first: 'Giải Nhất', second: 'Giải Nhì', third: 'Giải Ba' };
const PRIZE_COLOR = { first: 'gold', second: 'blue', third: 'orange' };

const SUBJECT_LABEL = {
    math: 'Toán',
    physics: 'Vật lý',
    chemistry: 'Hóa học',
    biology: 'Sinh học',
    literature: 'Ngữ văn',
    english: 'Tiếng Anh',
    history: 'Lịch sử',
    geography: 'Địa lý',
    civic_education: 'GDCD',
    technology: 'Công nghệ',
    computer_science: 'Tin học',
};

const buildColumns = (navigate) => [
    {
        title: 'Mã hồ sơ',
        dataIndex: 'student_code',
        width: 120,
        sorter: (a, b) => (a.student_code ?? '').localeCompare(b.student_code ?? ''),
        render: (v) => <Text code>{v}</Text>,
    },
    {
        title: 'Họ và tên',
        dataIndex: 'full_name',
        sorter: (a, b) => (a.full_name ?? '').localeCompare(b.full_name ?? ''),
    },
    {
        title: 'CCCD',
        dataIndex: 'identification',
        width: 150,
        render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
        title: 'Giải thưởng',
        dataIndex: 'provincial_prize',
        width: 130,
        filters: [
            { text: 'Giải Nhất', value: 'first' },
            { text: 'Giải Nhì', value: 'second' },
            { text: 'Giải Ba', value: 'third' },
        ],
        onFilter: (v, r) => r.provincial_prize === v,
        sorter: (a, b) => {
            const order = { first: 1, second: 2, third: 3 };
            return (order[a.provincial_prize] ?? 9) - (order[b.provincial_prize] ?? 9);
        },
        render: (v) => v
            ? <Tag color={PRIZE_COLOR[v] ?? 'default'}>{PRIZE_LABEL[v] ?? v}</Tag>
            : <Text type="secondary">—</Text>,
    },
    {
        title: 'Môn thi',
        dataIndex: 'provincial_prize_subject_id',
        width: 140,
        filters: Object.entries(SUBJECT_LABEL).map(([k, v]) => ({ text: v, value: k })),
        onFilter: (v, r) => r.provincial_prize_subject_id === v,
        render: (v) => v
            ? <Text>{SUBJECT_LABEL[v] ?? v}</Text>
            : <Text type="secondary">—</Text>,
    },
    {
        title: 'Khu vực',
        dataIndex: 'admission_area',
        width: 110,
        filters: [
            { text: 'KV1', value: 'KV1' },
            { text: 'KV2-NT', value: 'KV2-NT' },
            { text: 'KV2', value: 'KV2' },
            { text: 'KV3', value: 'KV3' },
        ],
        onFilter: (v, r) => r.admission_area === v,
        render: (v) => v
            ? <Tag color={AREA_TAG_COLOR[v] ?? 'default'}>{v}</Tag>
            : <Text type="secondary">—</Text>,
    },
    {
        title: 'Hành động',
        key: 'action',
        width: 100,
        align: 'center',
        render: (_, record) => (
            <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/student-profile/${record.id}`)}
            >
                Chi tiết
            </Button>
        ),
    },
];

const AdminProvincialPrizeList = () => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        axiosInstance.get('/api/v1/admin/students-with-provincial-prize')
            .then((res) => {
                if (res.data?.success) setData(res.data.data ?? []);
            })
            .finally(() => setLoading(false));
    }, []);

    const exportToExcel = () => {
        try {
            setExporting(true);

            const excelData = data.map((s) => {
                const baseData = {
                    'Mã hồ sơ': s.student_code,
                    'Họ và tên': s.full_name,
                    'CCCD': s.identification,
                    'Giải thưởng': PRIZE_LABEL[s.provincial_prize] ?? s.provincial_prize,
                    'Môn thi': SUBJECT_LABEL[s.provincial_prize_subject_id] ?? s.provincial_prize_subject_id,
                    'Khu vực': s.admission_area,
                };

                Object.values(s.attachments ?? {}).forEach((att) => {
                    baseData[`Tài liệu: ${att.type_name}`] = att.has_attachment ? att.file_name : '';
                });

                return baseData;
            });

            const ws = XLSX.utils.json_to_sheet(excelData);

            // json_to_sheet writes plain strings; turn attachment cells into
            // real HYPERLINK formulas so Excel renders them as clickable links.
            const fixedColumnCount = 6;
            const range = XLSX.utils.decode_range(ws['!ref']);
            for (let r = 0; r <= range.e.r - 1; r++) {
                const student = data[r];
                const attachments = Object.values(student.attachments ?? {});
                attachments.forEach((att, i) => {
                    if (!att.has_attachment) return;
                    const cellRef = XLSX.utils.encode_cell({ r: r + 1, c: fixedColumnCount + i });
                    ws[cellRef] = {
                        t: 'str',
                        f: `HYPERLINK("${att.download_url}","${att.file_name}")`,
                        v: att.file_name,
                    };
                });
            }

            ws['!cols'] = [
                { wch: 15 }, // Mã hồ sơ
                { wch: 25 }, // Họ và tên
                { wch: 15 }, // CCCD
                { wch: 14 }, // Giải thưởng
                { wch: 14 }, // Môn thi
                { wch: 12 }, // Khu vực
                { wch: 50 }, // Tài liệu: Giấy chứng nhận Giải thưởng cấp tỉnh
            ];

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'DS giải tỉnh');

            const now = new Date();
            const formattedDate = now.toLocaleDateString('vi-VN', {
                year: 'numeric', month: '2-digit', day: '2-digit'
            }).replace(/\//g, '-');
            const formattedTime = now.toLocaleTimeString('vi-VN', {
                hour: '2-digit', minute: '2-digit', second: '2-digit'
            }).replace(/:/g, '-');

            XLSX.writeFile(wb, `danh-sach-giai-thuong-cap-tinh-${formattedDate}_${formattedTime}.xlsx`);
            message.success('Xuất file Excel thành công!');
        } catch (error) {
            message.error('Có lỗi xảy ra khi xuất file Excel: ' + error.message);
        } finally {
            setExporting(false);
        }
    };

    return (
        <AdminLayout title="Thí sinh có Giải thưởng cấp tỉnh">
            <Space style={{ marginBottom: 16 }}>
                <Card size="small">
                    <Statistic
                        title="Tổng số thí sinh"
                        value={data.length}
                        prefix={<TrophyOutlined style={{ color: '#faad14' }} />}
                        loading={loading}
                    />
                </Card>
                <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={exportToExcel}
                    loading={exporting}
                    disabled={loading || data.length === 0}
                >
                    Xuất Excel
                </Button>
            </Space>
            <Table
                columns={buildColumns(navigate)}
                dataSource={data}
                rowKey="student_code"
                loading={loading}
                size="small"
                scroll={{ x: 800 }}
                pagination={{
                    pageSize: 20,
                    showTotal: (t) => `Tổng ${t} thí sinh`,
                    showSizeChanger: true,
                }}
            />
        </AdminLayout>
    );
};

export default AdminProvincialPrizeList;
